/**
 * Base Controller Module
 * Abstract base class for all page controllers
 *
 * Following OOA/OOD principles:
 * - Template Method Pattern: Defines skeleton of page initialization
 * - Single Responsibility: Each controller handles one page
 * - Dependency Injection: Services injected via constructor
 */

import { PageName, PageController } from '../types/page-types';
import { safeFeatherReplace } from '../../utils/feather-helper';
import type { CanisterClientInterface } from '../../canister-client';
import type { SettingsService } from '../pagination/pagination-controller';
import type { PaginationController } from '../pagination/pagination-controller';

/**
 * Services that controllers can use
 */
export interface ControllerServices {
    canisterClient: CanisterClientInterface;
    settingsService: SettingsService;
    paginationController?: PaginationController;
}

/**
 * Abstract base controller for pages
 * Provides common functionality and enforces interface
 */
export abstract class BaseController implements PageController {
    protected services: ControllerServices;
    protected isInitialized: boolean = false;

    constructor(services: ControllerServices) {
        this.services = services;
    }

    /**
     * Get the canister client
     */
    protected get canisterClient(): CanisterClientInterface {
        return this.services.canisterClient || window.canisterClient;
    }

    /**
     * Get the settings service
     */
    protected get settingsService(): SettingsService {
        return this.services.settingsService;
    }

    /**
     * Abstract method - must be implemented by subclasses
     */
    abstract getPageName(): PageName;

    /**
     * Template method for page initialization
     * Subclasses can override specific steps
     */
    public async init(): Promise<void> {
        console.log(`Initializing ${this.getPageName()} page`);

        try {
            // Pre-initialization hook
            await this.beforeInit();

            // Main initialization
            await this.doInit();

            // Setup event listeners
            this.setupEventListeners();

            // Refresh icons
            this.refreshIcons();

            // Post-initialization hook
            await this.afterInit();

            this.isInitialized = true;
            console.log(`${this.getPageName()} page initialized successfully`);
        } catch (error) {
            console.error(`Failed to initialize ${this.getPageName()}:`, error);
            throw error;
        }
    }

    /**
     * Cleanup when leaving the page
     */
    public cleanup(): void {
        console.log(`Cleaning up ${this.getPageName()} page`);
        this.doCleanup();
    }

    /**
     * Hook: Called before main initialization
     */
    protected async beforeInit(): Promise<void> {
        // Override in subclass if needed
    }

    /**
     * Abstract: Main initialization logic
     */
    protected abstract doInit(): Promise<void>;

    /**
     * Hook: Setup event listeners
     */
    protected setupEventListeners(): void {
        // Override in subclass if needed
    }

    /**
     * Hook: Called after main initialization
     */
    protected async afterInit(): Promise<void> {
        // Override in subclass if needed
    }

    /**
     * Hook: Cleanup logic
     */
    protected doCleanup(): void {
        // Override in subclass if needed
    }

    /**
     * Refresh Feather icons on the page
     */
    protected refreshIcons(): void {
        safeFeatherReplace();
    }

    /**
     * Helper: Update element text content
     */
    protected updateElementText(elementId: string, value: string | number): void {
        const element = document.getElementById(elementId);
        if (element) {
            element.textContent = String(value);
        }
    }

    /**
     * Helper: Escape HTML to prevent XSS
     */
    protected escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Helper: Show loading state for an element
     */
    protected showLoading(elementId: string, message: string = 'Loading...'): void {
        const element = document.getElementById(elementId);
        if (element) {
            element.innerHTML = `<div class="text-center py-4"><div class="animate-pulse">${message}</div></div>`;
        }
    }

    /**
     * Helper: Show error state for an element
     */
    protected showError(elementId: string, error: string): void {
        const element = document.getElementById(elementId);
        if (element) {
            element.innerHTML = `<div class="text-center text-red-500 py-4">${this.escapeHtml(error)}</div>`;
        }
    }
}
